import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  MetricsSummary,
  MonthlyTrendPoint,
  PacientesNoVolvieronResumen,
  PacientesRecurrencia,
} from '@/application/metrics';
import {
  getMonthlyTrend,
  getPacientesNoVolvieronResumen,
  getPacientesRecurrencia,
} from '@/infrastructure/http/metricsApi';
import { formatInteger } from '@/presentation/components/metrics/metricFormatters';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { previousMonthOf } from '@/presentation/hooks/useGlobalPeriod';
import { formatSigned, relativeChangePct } from '@/presentation/utils/metricDeltas';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import type { BranchMainMetrics } from './ClinicHubProfessionalsPanel';

export interface ClinicHubPatientsPanelProps {
  tenantId: string;
  period: string;
  year: number;
  month: number;
  summary: MetricsSummary | null;
  previousSummary: MetricsSummary | null;
  branchMainMetrics: BranchMainMetrics[];
  isBranchMetricsLoading?: boolean;
  /** Si hay sede del hub, el riesgo (no_volvieron) sigue siendo de toda la clínica. */
  consultorioId?: number;
}

type SignalTone = 'up' | 'down' | 'flat' | 'neutral';

function toneHigherBetter(delta: number | null, band = 1.5): SignalTone {
  if (delta === null || Math.abs(delta) < band) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

function toneLowerBetter(delta: number | null, band = 0.5): SignalTone {
  if (delta === null || Math.abs(delta) < band) return 'flat';
  return delta < 0 ? 'up' : 'down';
}

function toneClass(tone: SignalTone): string {
  if (tone === 'up') return 'text-teal-700 dark:text-teal-300';
  if (tone === 'down') return 'text-red-700 dark:text-red-300';
  return 'text-slate-500 dark:text-slate-400';
}

function ppDelta(current: number, previous: number | null): number | null {
  if (previous === null) return null;
  return Number((current - previous).toFixed(1));
}

function absDelta(current: number, previous: number | null): number | null {
  if (previous === null) return null;
  return current - previous;
}

/** Mini sparkline SVG a partir de una serie (más oscuro = más reciente). */
function Sparkline({ values, className }: { values: number[]; className?: string }) {
  const w = 160;
  const h = 36;
  const pad = 2;
  if (values.length < 2) {
    return <div className={className} style={{ height: h }} />;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={className}
      width={w}
      height={h}
      aria-hidden
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
        className="text-teal-600 dark:text-teal-400"
      />
    </svg>
  );
}

/**
 * Tab Pacientes del hub: adquisición → continuidad → riesgo.
 * Solo muestra métricas con scope confiable; por sucursal solo atendidos.
 */
export function ClinicHubPatientsPanel({
  tenantId,
  period,
  year,
  month,
  summary,
  previousSummary,
  branchMainMetrics,
  isBranchMetricsLoading,
  consultorioId,
}: ClinicHubPatientsPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { token } = useAuth();
  const { hasPermission } = usePermissions();

  const [recurrence, setRecurrence] = useState<PacientesRecurrencia | null>(null);
  const [prevRecurrence, setPrevRecurrence] = useState<PacientesRecurrencia | null>(null);
  const [risk, setRisk] = useState<PacientesNoVolvieronResumen | null>(null);
  const [prevRisk, setPrevRisk] = useState<PacientesNoVolvieronResumen | null>(null);
  const [trend, setTrend] = useState<MonthlyTrendPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const canRecurrence = hasPermission('metrics.recurrencia');
  const canNoReturn = hasPermission('metrics.no_volvieron');
  const canPatients = hasPermission('metrics.pacientes');

  const prev = useMemo(() => previousMonthOf(year, month), [year, month]);
  const prevMonthLabel = useMemo(() => {
    try {
      return new Date(prev.year, prev.month - 1, 1).toLocaleDateString(locale, {
        month: 'long',
      });
    } catch {
      return '';
    }
  }, [prev, locale]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !tenantId) {
      setRecurrence(null);
      setPrevRecurrence(null);
      setRisk(null);
      setPrevRisk(null);
      setTrend([]);
      return;
    }

    void (async () => {
      setIsLoading(true);
      try {
        const jobs: Promise<void>[] = [];

        if (canRecurrence) {
          jobs.push(
            Promise.allSettled([
              getPacientesRecurrencia(token, { tenantId, year, month }),
              getPacientesRecurrencia(token, {
                tenantId,
                year: prev.year,
                month: prev.month,
              }),
            ]).then(([cur, previous]) => {
              if (cancelled) return;
              setRecurrence(cur.status === 'fulfilled' ? cur.value : null);
              setPrevRecurrence(previous.status === 'fulfilled' ? previous.value : null);
            })
          );
        } else {
          setRecurrence(null);
          setPrevRecurrence(null);
        }

        if (canNoReturn) {
          jobs.push(
            Promise.allSettled([
              getPacientesNoVolvieronResumen(token, { tenantId, year, month }),
              getPacientesNoVolvieronResumen(token, {
                tenantId,
                year: prev.year,
                month: prev.month,
              }),
            ]).then(([cur, previous]) => {
              if (cancelled) return;
              setRisk(cur.status === 'fulfilled' ? cur.value : null);
              setPrevRisk(previous.status === 'fulfilled' ? previous.value : null);
            })
          );
        } else {
          setRisk(null);
          setPrevRisk(null);
        }

        if (canPatients) {
          jobs.push(
            getMonthlyTrend(token, { tenantId, year, month, months: 6 })
              .then((points) => {
                if (!cancelled) setTrend(points);
              })
              .catch(() => {
                if (!cancelled) setTrend([]);
              })
          );
        } else {
          setTrend([]);
        }

        await Promise.all(jobs);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, tenantId, year, month, prev.year, prev.month, canRecurrence, canNoReturn, canPatients]);

  const qsDomain = `period=${period}&tenant=${tenantId}`;
  const qsReport = `tenant=${tenantId}&period=${period}&year=${year}&month=${month}`;

  const nuevos = summary?.pacientes.nuevos ?? null;
  const prevNuevos = previousSummary?.pacientes.nuevos ?? null;
  const nuevosDelta = nuevos !== null && prevNuevos !== null ? relativeChangePct(nuevos, prevNuevos) : null;

  const atendidos = summary?.pacientes.atendidos ?? recurrence?.atendidos ?? null;
  const prevAtendidos = previousSummary?.pacientes.atendidos ?? prevRecurrence?.atendidos ?? null;
  const atendidosDelta =
    atendidos !== null && prevAtendidos !== null ? relativeChangePct(atendidos, prevAtendidos) : null;

  const retentionRate = recurrence?.tasaRecurrencia ?? null;
  const retentionDelta = ppDelta(retentionRate ?? 0, prevRecurrence?.tasaRecurrencia ?? null);
  // si no hay tasa actual, no mostrar delta ficticio
  const retentionDeltaSafe = retentionRate === null ? null : retentionDelta;

  const riskTotal = risk?.total ?? null;
  const riskDelta = absDelta(riskTotal ?? 0, prevRisk?.total ?? null);
  const riskDeltaSafe = riskTotal === null ? null : riskDelta;

  const sparkValues = useMemo(
    () => trend.map((p) => p.pacientesNuevos),
    [trend]
  );

  const branchRows = useMemo(() => {
    return branchMainMetrics
      .map((row) => ({
        id: row.sede.id,
        name: row.sede.nombre,
        patients: row.summary?.pacientes.atendidos ?? null,
      }))
      .filter((r) => r.patients !== null)
      .sort((a, b) => (b.patients ?? 0) - (a.patients ?? 0));
  }, [branchMainMetrics]);

  if (isLoading && !summary) {
    return <LoadingBlock minHeightClassName="min-h-[12rem]" />;
  }

  return (
    <div className="space-y-8 pb-8">
      {/* KPI strip */}
      <section aria-label={t('clinicHub.patientsTab.kpisAria')}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
          <Kpi
            value={nuevos === null ? '—' : formatInteger(nuevos, locale)}
            label={t('clinicHub.patientsTab.kpis.new')}
            delta={
              nuevosDelta === null
                ? null
                : t('clinicHub.patientsTab.deltaPct', {
                    value: formatSigned(locale, nuevosDelta),
                  })
            }
            tone={toneHigherBetter(nuevosDelta)}
          />
          <Kpi
            value={atendidos === null ? '—' : formatInteger(atendidos, locale)}
            label={t('clinicHub.patientsTab.kpis.seen')}
            delta={
              atendidosDelta === null
                ? null
                : t('clinicHub.patientsTab.deltaPct', {
                    value: formatSigned(locale, atendidosDelta),
                  })
            }
            tone={toneHigherBetter(atendidosDelta)}
          />
          <Kpi
            value={
              retentionRate === null
                ? '—'
                : `${retentionRate.toLocaleString(locale, { maximumFractionDigits: 1 })}%`
            }
            label={t('clinicHub.patientsTab.kpis.retention')}
            delta={
              retentionDeltaSafe === null
                ? null
                : t('clinicHub.patientsTab.deltaPts', {
                    value: formatSigned(locale, retentionDeltaSafe),
                    month: prevMonthLabel,
                  })
            }
            tone={toneHigherBetter(retentionDeltaSafe, 0.5)}
            hint={t('clinicHub.patientsTab.kpis.retentionHint')}
          />
          <Kpi
            value={riskTotal === null ? '—' : formatInteger(riskTotal, locale)}
            label={t('clinicHub.patientsTab.kpis.risk')}
            delta={
              riskDeltaSafe === null
                ? null
                : t('clinicHub.patientsTab.deltaAbs', {
                    value: formatSigned(locale, riskDeltaSafe, 0),
                  })
            }
            tone={toneLowerBetter(riskDeltaSafe !== null ? riskDeltaSafe : null, 0.5)}
          />
        </div>
      </section>

      {/* Adquisición */}
      <section className="space-y-2 border-t border-[var(--bi-line)] pt-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('clinicHub.patientsTab.acquisition.title')}
        </h2>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {nuevos === null
                ? '—'
                : t('clinicHub.patientsTab.acquisition.headline', {
                    value: formatInteger(nuevos, locale),
                  })}
            </p>
            {nuevosDelta !== null && (
              <p className={`mt-1 text-sm ${toneClass(toneHigherBetter(nuevosDelta))}`}>
                {t('clinicHub.patientsTab.deltaPct', {
                  value: formatSigned(locale, nuevosDelta),
                })}
              </p>
            )}
            <p className="mt-1 max-w-md text-xs text-gray-500 dark:text-gray-400">
              {t('clinicHub.patientsTab.acquisition.hint')}
            </p>
          </div>
          {sparkValues.length >= 2 ? (
            <div className="text-right">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {t('clinicHub.patientsTab.acquisition.sparkLabel')}
              </p>
              <Sparkline values={sparkValues} />
            </div>
          ) : null}
        </div>
        <Link
          to={`/patients?${qsDomain}&tab=acquisition`}
          className="inline-block text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('clinicHub.patientsTab.acquisition.cta')} →
        </Link>
      </section>

      {/* Retención */}
      <section className="space-y-2 border-t border-[var(--bi-line)] pt-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('clinicHub.patientsTab.retention.title')}
        </h2>
        {retentionRate === null ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('clinicHub.patientsTab.retention.unavailable')}
          </p>
        ) : (
          <>
            <p className="text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {t('clinicHub.patientsTab.retention.headline', {
                rate: retentionRate.toLocaleString(locale, { maximumFractionDigits: 1 }),
              })}
            </p>
            {retentionDeltaSafe !== null && (
              <p className={`text-sm ${toneClass(toneHigherBetter(retentionDeltaSafe, 0.5))}`}>
                {t('clinicHub.patientsTab.deltaPts', {
                  value: formatSigned(locale, retentionDeltaSafe),
                  month: prevMonthLabel,
                })}
              </p>
            )}
            {recurrence && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('clinicHub.patientsTab.retention.detail', {
                  recurrent: formatInteger(recurrence.recurrentes, locale),
                  first: formatInteger(recurrence.nuevos, locale),
                  seen: formatInteger(recurrence.atendidos, locale),
                })}
              </p>
            )}
          </>
        )}
        <Link
          to={
            canRecurrence
              ? `/reports/conversion?${qsReport}`
              : `/patients?${qsDomain}&tab=retention`
          }
          className="inline-block text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('clinicHub.patientsTab.retention.cta')} →
        </Link>
      </section>

      {/* Riesgo */}
      <section className="space-y-2 border-t border-[var(--bi-line)] pt-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          {t('clinicHub.patientsTab.risk.title')}
        </h2>
        {consultorioId != null && (
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('clinicHub.patientsTab.risk.clinicWideNote')}</p>
        )}
        {riskTotal === null ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('clinicHub.patientsTab.risk.unavailable')}
          </p>
        ) : (
          <>
            <p className="text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {t('clinicHub.patientsTab.risk.headline', {
                value: formatInteger(riskTotal, locale),
              })}
            </p>
            {riskDeltaSafe !== null && (
              <p className={`text-sm ${toneClass(toneLowerBetter(riskDeltaSafe, 0.5))}`}>
                {t('clinicHub.patientsTab.deltaAbs', {
                  value: formatSigned(locale, riskDeltaSafe, 0),
                })}
              </p>
            )}
            {risk && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('clinicHub.patientsTab.risk.detail', {
                  exhausted: formatInteger(risk.conPlanAgotado, locale),
                  desde: risk.desde,
                  hasta: risk.hasta,
                })}
              </p>
            )}
          </>
        )}
        <Link
          to={canNoReturn ? `/reports/no-return?${qsReport}` : `/patients?${qsDomain}&tab=risk`}
          className="inline-block text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('clinicHub.patientsTab.risk.cta')} →
        </Link>
      </section>

      {/* Por sucursal — solo atendidos (scope real) */}
      {branchMainMetrics.length > 1 ? (
        <section className="space-y-2 border-t border-[var(--bi-line)] pt-6">
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
            {t('clinicHub.patientsTab.branches.title')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('clinicHub.patientsTab.branches.hint')}
          </p>
          {isBranchMetricsLoading ? (
            <LoadingBlock size="sm" minHeightClassName="min-h-[4rem]" />
          ) : branchRows.length === 0 ? (
            <p className="text-sm text-gray-500">{t('clinicHub.patientsTab.branches.empty')}</p>
          ) : (
            <div className="max-w-md overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="pb-1.5 pr-4 font-semibold">
                      {t('clinicHub.patientsTab.branches.cols.branch')}
                    </th>
                    <th className="pb-1.5 font-semibold tabular-nums">
                      {t('clinicHub.patientsTab.branches.cols.seen')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {branchRows.map((row) => (
                    <tr key={row.id} className="border-t border-[var(--bi-line)] first:border-0">
                      <td className="py-1.5 pr-4 font-medium text-gray-900 dark:text-gray-100">
                        {row.name}
                      </td>
                      <td className="py-1.5 tabular-nums text-gray-800 dark:text-gray-200">
                        {formatInteger(row.patients ?? 0, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      <div className="border-t border-[var(--bi-line)] pt-4">
        <Link
          to={`/patients?${qsDomain}`}
          className="text-sm font-semibold text-teal-700 hover:underline dark:text-teal-300"
        >
          {t('clinicHub.patientsTab.openAll')} →
        </Link>
      </div>
    </div>
  );
}

function Kpi({
  value,
  label,
  delta,
  tone,
  hint,
}: {
  value: string;
  label: string;
  delta: string | null;
  tone: SignalTone;
  hint?: string;
}) {
  return (
    <div>
      <p className="text-2xl font-semibold tabular-nums tracking-tight text-gray-950 dark:text-white sm:text-3xl">
        {value}
      </p>
      <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{label}</p>
      {delta ? <p className={`mt-0.5 text-xs font-medium ${toneClass(tone)}`}>{delta}</p> : null}
      {hint ? <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{hint}</p> : null}
    </div>
  );
}
